import { Divider, Drawer, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';

import type { Block, ContentVisibility } from '@/types/database';
import { getBlockContent } from '@/types/database';
import { blockRegistry } from '@/components/blocks/registry';
import type { Json } from '@/integrations/supabase/types';

interface BlockEditPanelProps {
  block: Block | null;
  pageVisibility?: ContentVisibility;
  onClose: () => void;
  onUpdate: (blockId: string, content: Json) => void;
  onUpdateVisibility?: (blockId: string, visibility: ContentVisibility) => void;
}

export function BlockEditPanel({ block, pageVisibility, onClose, onUpdate, onUpdateVisibility }: BlockEditPanelProps) {
  const entry = block ? blockRegistry[block.block_type] : null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(block && entry)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 380 }, p: 2.5 } } }}
    >
      {block && entry ? (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Edit {entry.label}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close editor">
              <XIcon size={18} />
            </IconButton>
          </Stack>
          {onUpdateVisibility ? (
            <>
              <TextField
                select
                size="small"
                label="Who can see this block"
                value={block.visibility}
                onChange={(event) => onUpdateVisibility(block.id, event.target.value as ContentVisibility)}
                helperText={
                  pageVisibility === 'internal'
                    ? 'This page is internal-only, so block visibility has no effect until the page itself is made public.'
                    : block.visibility === 'internal'
                      ? 'Only signed-in unit members can see this block, even on a public page.'
                      : undefined
                }
                fullWidth
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="internal">Internal — unit members only</MenuItem>
              </TextField>
              <Divider />
            </>
          ) : null}
          <entry.EditPanel
            key={block.id}
            content={getBlockContent(block)}
            onUpdate={(content) => onUpdate(block.id, content as unknown as Json)}
          />
        </Stack>
      ) : null}
    </Drawer>
  );
}
